import { BanknotesIcon, GiftIcon, ShareIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { memo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { T } from '../../../translation/src';
import { ReferralRole } from '../../../types/graphqlTypes';
import { HeroIcon } from '../../../types';
import { ordersForCustomers } from './partnerRoles';

const Step: React.FC<{ number: number; icon: HeroIcon; title: React.ReactNode; text: React.ReactNode }> = ({ number, icon: Icon, title, text }) => (
  <div className="relative flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-[#242424]">
    <span className="absolute right-4 top-3 text-4xl font-black text-gray-100 dark:text-gray-800">{number}</span>
    <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-dark-green-700 dark:border-white/10 dark:bg-white/5 dark:text-neon-green-400">
      <Icon className="h-5 w-5" />
    </span>
    <span className="font-semibold">{title}</span>
    <span className="text-sm text-gray-600 dark:text-gray-300">{text}</span>
  </div>
);

// The three steps of the programme, in the words of the role.
const HowItWorks_: React.FC<{ role: ReferralRole }> = ({ role }) => {
  const { organisationId = '' } = useParams();
  const customer = role === ReferralRole.Customer;
  const orders = ordersForCustomers(role);
  return (
    <div className="flex flex-col gap-3">
      <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">
        <T _str="how it works" />
      </span>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {/* A customer spreads the word; a partner brings in its own customers. */}
        <Step
          number={1}
          icon={ShareIcon}
          title={orders ? <T _str="Add your customers" /> : <T _str="Share your link" />}
          text={
            customer ? (
              <T _str="Send it to anyone who could use Pointorama: a colleague, a partner firm, a client." />
            ) : orders ? (
              <>
                <T _str="Create their organisation in your" />{' '}
                <Link to={`/organisations/${organisationId}/referrals/customers`} className="underline text-dark-green-700 hover:text-black dark:text-neon-green-400 dark:hover:text-white">
                  <T _str="customer center" />
                </Link>
                , <T _str="or send them your registration link. Either way they are linked to you." />
              </>
            ) : (
              <T _str="Send it to the customers you sell to. Anyone who signs up through it becomes your customer." />
            )
          }
        />
        <Step
          number={2}
          icon={UserPlusIcon}
          title={orders ? <T _str="You order for them" /> : <T _str="They sign up" />}
          text={
            orders ? (
              <T _str="They use Pointorama in their own organisation. You order their subscription, upgrades and extra projects, and set what you charge them." />
            ) : (
              <T _str="Their new organisation gets an extra free project. It is linked to yours the moment they register through your link." />
            )
          }
        />
        <Step
          number={3}
          icon={customer ? GiftIcon : BanknotesIcon}
          title={customer ? <T _str="You earn projects" /> : orders ? <T _str="An invoice per order" /> : <T _str="You earn your commission" />}
          text={
            customer ? (
              <T _str="Every subscription they take gives you a permanent project once their invoice is paid. Bonus projects come at each milestone. Individual project purchases do not count." />
            ) : orders ? (
              <T _str="Every order gets its own invoice, in your name, with your discount taken off. Subscriptions renew automatically. You hear about each renewal a month ahead." />
            ) : (
              <T _str="Their first subscription earns you your commission, paid out once their invoice is paid. After that they are a customer of Pythagoras." />
            )
          }
        />
      </div>
    </div>
  );
};
export const HowItWorks = memo(HowItWorks_);
